import React from 'react';
import { UserContext } from 'contexts/user/user-context';
import { UserDataContext } from 'contexts/user-data/user-data-context';
import usePrevious from 'utils/use-previous';


const defaultState = {
  activeTour: -1,
  tourStep: -1,
  runTour: false,
  toursSeen: [],
  beaconViews: {},
};


export const TourContext = React.createContext();


export const TourProvider = ({ children }) => {
  const { user } = React.useContext(UserContext);
  const { userData, setUserData } = React.useContext(UserDataContext);
  const prevUserData = usePrevious(userData);
  const [state, setState] = React.useState(defaultState);


  // initialize tour data from user data
  React.useEffect(() => {
    if (userData && !prevUserData && (userData.toursSeen || userData.beaconViews)) {
      setState(p => ({
        ...p,
        toursSeen: userData.toursSeen ?? p.toursSeen,
        beaconViews: userData.beaconViews ?? p.beaconViews
      }));
    }
  }, [userData, prevUserData]);


  const setActiveTour = React.useCallback((activeTour) => {
    setState(p => ({
      ...p,
      activeTour
    }));
  }, []);


  const setTourStep = React.useCallback((tourStep) => {
    setState(p => ({
      ...p,
      tourStep
    }));
  }, []);


  const setRunTour = React.useCallback((runTour) => {
    setState(p => ({
      ...p,
      runTour
    }));
  }, []);


  const setToursSeen = React.useCallback((tourKey) => {
    setState(p => {
      const toursSeen = [...p.toursSeen];
      if (!toursSeen.includes(tourKey)) {
        toursSeen.push(tourKey);
        setUserData({ toursSeen });
      }
      return {
        ...p,
        toursSeen
      };
    });
  }, [setUserData]);


  const updateBeaconViews = React.useCallback((tourKey) => {
    setState(p => {
      const beaconViews = {...p.beaconViews};
      if (beaconViews[tourKey] === undefined) {
        beaconViews[tourKey] = 0;
      }
      beaconViews[tourKey] += 1;
      setUserData({ beaconViews });
      return {
        ...p,
        beaconViews
      };
    });
  }, [setUserData]);


  const resetTours = React.useCallback(() => {
    setState(p => {
      setUserData({ toursSeen: undefined, beaconViews: undefined });
      return {
        ...p,
        toursSeen: [],
        beaconViews: {}
      }
    });
  }, [setUserData]);


  React.useEffect(() => {
    if (!user) {
      setState(defaultState);
    }
  }, [user]);


  const value = React.useMemo(() => ({
    ...state,
    setActiveTour,
    setTourStep,
    setRunTour,
    setToursSeen,
    updateBeaconViews,
    resetTours,
  }), [
    state,
    setActiveTour,
    setTourStep,
    setRunTour,
    setToursSeen,
    updateBeaconViews,
    resetTours
  ]);
  

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}